import React, { type FC, useState } from 'react'
import cn from 'classnames'

import { isNil } from 'helpers/utils'
import { type BaseProps } from 'types'

import styles from './style.scss'

export interface Props extends BaseProps {
  height?: number
  width?: number
  imageUrl?: string
  className?: string
  imgClassName?: string
  style?: React.CSSProperties
  alternateText?: string
  multiplier?: number
  placeholder?: React.ReactElement
  fromCache?: boolean
}

const defaultPlaceholder =
  'https://img1a.flixcart.com/batman-returns/batman-returns/p/images/fk-default-image-75ff34.png'

const RUKMINI_REGEX = /(\/FK_RUKUMINI)/i
const STATIC_WEB_REGEX = /(\/FK_STATIC_ASSET)/i

const Image: FC<Props> = ({
  height,
  width,
  imageUrl,
  className,
  imgClassName,
  alternateText = 'image',
  style,
  multiplier = 5,
  placeholder,
  fromCache = false,
}) => {
  if (isNil(imageUrl)) {
    return null
  }

  if (!isNil(width) && !isNil(height)) {
    imageUrl = imageUrl
      .replace(RUKMINI_REGEX, 'https://rukminim1.flixcart.com')
      .replace(STATIC_WEB_REGEX, 'https://static-assets-web.flixcart.com')
      .replace('{@width}', `${width * multiplier}`)
      .replace('{@height}', `${height * multiplier}`)
      .replace('{@quality}', '90')
  }

  const [hasImageLoaded, setHasImageLoaded] = useState(false)

  const handleLoad = (): void => {
    setHasImageLoaded(true)
  }

  let placeholderElement
  if (placeholder) {
    placeholderElement = placeholder
  } else {
    placeholderElement = (
      <img
        src={defaultPlaceholder}
        className={cn(imgClassName, styles['placeholder'])}
        style={style}
        width={'100%'}
        height={'100%'}
      />
    )
  }

  return (
    <div
      className={cn(className, styles['image'])}
      style={!hasImageLoaded ? { width, height } : {}}
    >
      <img
        src={imageUrl}
        alt={alternateText}
        className={imgClassName}
        style={style}
        width={width}
        height={height}
        onLoad={handleLoad}
        loading={!fromCache ? 'lazy' : 'eager'}
      />
      {!hasImageLoaded && placeholderElement}
    </div>
  )
}

export default Image
